import React, { FC, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useSubscriptionStateQuery,
  useBillingProfileQuery,
  usePaymentMethodsQuery,
  useSubscriptionPlansQuery,
  useValidatePromoCodeMutation,
  useReactivateSubscriptionMutation,
  useCheckoutPreviewQuery,
  useRecordLegalAcknowledgementMutation,
} from 'src/api/subscription/useSubscriptionApi';
import { useSubscriptionStore } from 'src/store/subscriptionStore';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as CheckmarkSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/checkmark.svg';
import { ReactComponent as SecurePaymentLockSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/secure-payment-lock.svg';
import { ReactComponent as BillingInfoEditSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/reactivate-pro/billing-info-edit.svg';
import { ReactComponent as AddButtonSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/add-button.svg';
import { CardBrandLogo } from '../components/CardBrandLogo';
import { formatMaskedCardNumber, getCardBrandDisplayName } from 'src/utils/cardUtils';
import { BillingInfoModal } from '../modals/BillingInfoModal';
import { PaymentMethodModal } from '../modals/PaymentMethodModal';
import { PaymentConfirmedModal } from '../modals/PaymentConfirmedModal';
import { toast } from 'src/utils/appAlert';
import { Spin } from 'antd';
import { LoadingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import './ReactivatePro.scss';

export const ReactivatePro: FC = () => {
  const navigate = useNavigate();
  const { data: subState } = useSubscriptionStateQuery();
  const { data: billingProfile, refetch: refetchBillingProfile } = useBillingProfileQuery();
  const { data: paymentMethodsData, refetch: refetchPaymentMethods } = usePaymentMethodsQuery();
  const {
    billingCycle: storeBillingCycle,
    billingInfo: storeBillingInfo,
    paymentMethods: storePaymentMethods,
    reactivateSubscription: storeReactivate,
    appliedPromo: storeAppliedPromo,
    removePromoCode: storeRemovePromo,
  } = useSubscriptionStore();

  const profileBillingCountry = billingProfile?.country || storeBillingInfo.country;
  const { data: plansData, isLoading: isPlansLoading } =
    useSubscriptionPlansQuery(profileBillingCountry);
  const billingCountry = profileBillingCountry || plansData?.country;
  const { mutate: validatePromo, isPending: isValidatingPromo } = useValidatePromoCodeMutation();
  const { mutate: reactivateSub, isPending: isSubmitting } = useReactivateSubscriptionMutation();
  const recordLegalAck = useRecordLegalAcknowledgementMutation();

  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isConfirmedModalOpen, setIsConfirmedModalOpen] = useState(false);

  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [appliedPromoState, setAppliedPromoState] = useState<{
    code: string;
    discountPercent: number;
  } | null>(null);
  const [isAgreed, setIsAgreed] = useState(false);
  const [agreedError, setAgreedError] = useState(false);

  const billingCycle =
    subState?.billing_interval === 'year' ? 'yearly' : storeBillingCycle || 'monthly';
  const targetInterval = billingCycle === 'yearly' ? 'year' : 'month';
  const proPlan =
    plansData?.plans?.find((p) => p.tier_slug === 'pro' && p.interval === targetInterval) ||
    plansData?.plans?.find((p) => p.tier_slug === 'pro') ||
    plansData?.plans?.[0];

  const featuresList = proPlan?.features?.length ? proPlan.features : [];
  const activePromo = appliedPromoState || storeAppliedPromo;
  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
    error: previewError,
    refetch: refetchPreview,
  } = useCheckoutPreviewQuery({
    tier_slug: 'pro',
    interval: targetInterval,
    price_id: proPlan?.price_id,
    promo_code: activePromo?.code,
    country: billingCountry,
    trial_selection: false,
    enabled: Boolean(proPlan?.price_id),
  });

  const backendPromoLabel = previewData?.promotion_percentage
    ? ` (-${previewData.promotion_percentage}%)`
    : previewData?.formatted_promotion_discount_amount || previewData?.formatted_discount
      ? ` (-${previewData.formatted_promotion_discount_amount || previewData.formatted_discount})`
      : '';

  const [currentTimestamp] = useState(() => Date.now());
  const rawAccessUntil = subState?.access_until || subState?.current_period_end;
  const isPlanNotExpired = useMemo(() => {
    const isCancelledOrPending =
      subState?.status === 'cancelled' || subState?.cancel_at_period_end === true;
    if (!isCancelledOrPending || !rawAccessUntil) return false;
    const accessTime = new Date(rawAccessUntil).getTime();
    return !isNaN(accessTime) && accessTime > currentTimestamp;
  }, [subState?.status, subState?.cancel_at_period_end, rawAccessUntil, currentTimestamp]);

  const accessUntilFormatted = useMemo(() => {
    if (!rawAccessUntil) return '';
    try {
      const d = new Date(rawAccessUntil);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  }, [rawAccessUntil]);

  const currencySymbol =
    previewData?.currency_symbol ||
    plansData?.currency_symbol ||
    previewData?.formatted_total?.match(/^[^\d\s]+/)?.[0] ||
    '₹';

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription/my-subscription');
    }
  };

  const handleApplyPromo = () => {
    const trimmed = promoInput.trim().toUpperCase();
    if (!trimmed || isValidatingPromo) {
      if (!trimmed) {
        setPromoError('Please enter a promo code');
      }
      return;
    }
    if (trimmed.length < 3) {
      setPromoError('Promo code must be at least 3 characters');
      return;
    }
    if (trimmed.length > 30) {
      setPromoError('Promo code cannot exceed 30 characters');
      return;
    }
    setPromoError('');
    validatePromo(
      {
        code: trimmed,
        tier_slug: 'pro',
        interval: targetInterval,
        price_id: proPlan?.price_id,
        country: billingCountry,
      },
      {
        onSuccess: (res) => {
          const promoCode = res.applied_promo?.code || res.code || trimmed;
          const promoDiscount =
            res.applied_promo?.discount_percent ?? res.discount_percent ?? res.percent_off ?? 0;
          setAppliedPromoState({
            code: promoCode,
            discountPercent: promoDiscount,
          });
          toast.success(res.message || `Promo code ${promoCode} applied!`);
          setPromoInput('');
          setPromoError('');
        },
        onError: (err: any) => {
          setPromoError(err.userMessage || err.message || 'Invalid promo code');
        },
      },
    );
  };

  const handleRemovePromo = () => {
    setAppliedPromoState(null);
    storeRemovePromo();
  };

  const handleReactivate = () => {
    if (!isAgreed) {
      setAgreedError(true);
      toast.error('Please agree to the Terms of Service and Privacy Policy.');
      return;
    }

    setAgreedError(false);
    reactivateSub(undefined, {
      onSuccess: (res) => {
        recordLegalAck.mutate();
        storeReactivate();
        setIsConfirmedModalOpen(true);
        toast.success(res.message || 'Welcome back! Your Pro subscription has been reactivated.');
      },
      onError: (err: any) => {
        if (err.code === 'ERR_SUBSCRIPTION_ALREADY_EXPIRED') {
          toast.error('Your subscription has expired. Please select a plan to renew Pro.');
          navigate('/onboarding-trainer/dashboard/subscription/plans');
        } else {
          toast.error(err.message || 'Failed to reactivate subscription.');
        }
      },
    });
  };

  const handleGoToSupport = () => {
    navigate('/onboarding-trainer/dashboard/subscription/contact-support');
  };

  const handleNavigateLegal = (path: string) => {
    navigate(`/onboarding-trainer/dashboard/subscription/${path}`);
  };

  const paymentCards = paymentMethodsData?.payment_methods || [];
  const defaultCard: any =
    paymentCards.find((c: any) => c.id === paymentMethodsData?.default_payment_method_id) ||
    paymentCards.find((c: any) => c.is_default || c.isDefault) ||
    paymentCards[0] ||
    storePaymentMethods.find((c) => c.isDefault) ||
    storePaymentMethods[0];

  const fullNameDisplay =
    billingProfile?.fullName ||
    billingProfile?.companyName ||
    storeBillingInfo.fullName ||
    storeBillingInfo.companyName ||
    'Personal Trainer';
  const emailDisplay = billingProfile?.billingEmail || storeBillingInfo.billingEmail || '';
  const addressDisplay = billingProfile?.addressLine1
    ? `${billingProfile.addressLine1}${billingProfile.city ? `, ${billingProfile.city}` : ''}`
    : storeBillingInfo.addressLine1;
  if (isPlansLoading || isPreviewLoading) {
    return (
      <div
        className="reactivate-page-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#E2F163' }} spin />} />
      </div>
    );
  }

  return (
    <div className="reactivate-page-container">
      {/* Header */}
      <div className="reactivate-page-header">
        <button type="button" className="reactivate-back-btn" onClick={handleBack}>
          <UserChevronLeftSVG />
          <span>{'Reactivate Pro'}</span>
        </button>
        <p className="reactivate-subtitle">
          {'Welcome back! Reactivate your Pro plan to regain access to all features'}
        </p>
      </div>

      {/* 2-Column Grid */}
      <div className="reactivate-grid">
        {/* Left Column */}
        <div className="reactivate-left-col">
          {/* Unexpired Plan Reassurance Banner */}
          {isPlanNotExpired && (
            <div className="reactivate-unexpired-banner">
              <div className="banner-icon-wrap">
                <InfoCircleOutlined className="banner-icon" />
              </div>
              <div className="banner-content">
                <div className="banner-header">
                  <span className="banner-title">{'No charge today'}</span>
                  {accessUntilFormatted && (
                    <span className="banner-pill">{`Active until ${accessUntilFormatted}`}</span>
                  )}
                </div>
                <p className="banner-desc">
                  {'You already have active Pro access until '}
                  <strong>{accessUntilFormatted || 'the end of your billing cycle'}</strong>
                  {
                    '. Reactivating keeps all your Pro features active without interruption, and your automatic renewal will resume on '
                  }
                  <strong>{accessUntilFormatted || 'your next billing date'}</strong>
                  {'.'}
                </p>
              </div>
            </div>
          )}

          {/* Card 1: Billing Summary */}
          <div className="reactivate-card summary-card">
            <h3>{'Billing Summary'}</h3>

            <div className="summary-rows">
              {isPreviewError ? (
                <div
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#ef4444',
                    fontSize: '13px',
                    marginBottom: '12px',
                  }}
                >
                  <p style={{ margin: '0 0 6px 0' }}>
                    {(previewError as any)?.userMessage ||
                      (previewError as any)?.message ||
                      'Failed to load pricing preview.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchPreview()}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: 0,
                      fontWeight: 600,
                    }}
                  >
                    {'Retry'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="summary-row">
                    <span className="row-label">{proPlan?.name || 'Pro Plan'}</span>
                    <span className="row-value">{previewData?.formatted_subtotal || '—'}</span>
                  </div>
                  <div className="summary-row cycle-row">
                    <span className="row-label">{'Billing Cycle'}</span>
                    <span className="row-value capitalize">{billingCycle}</span>
                  </div>
                  {activePromo && previewData?.formatted_discount && (
                    <div className="summary-row discount-row">
                      <span className="row-label">{`Promo Discount (${activePromo.code})`}</span>
                      <span className="row-value" style={{ color: '#10b981' }}>
                        {`-${previewData.formatted_discount}`}
                      </span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span className="row-label">{'Subtotal'}</span>
                    <span className="row-value">
                      {activePromo && previewData?.formatted_discount ? (
                        <>
                          <span className="strikethrough">{previewData.formatted_subtotal}</span>{' '}
                          {previewData.formatted_discounted_subtotal || previewData.formatted_total}
                        </>
                      ) : (
                        previewData?.formatted_subtotal || '—'
                      )}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="row-label">{`Tax (${previewData?.tax_rate_percent ?? 0}%)`}</span>
                    <span className="row-value">{previewData?.formatted_tax || '—'}</span>
                  </div>
                  {isPlanNotExpired ? (
                    <>
                      <div className="summary-row next-charge-row">
                        <span className="row-label">
                          {accessUntilFormatted
                            ? `Renewal Total (on ${accessUntilFormatted})`
                            : 'Renewal Total'}
                        </span>
                        <span className="row-value">{previewData?.formatted_total || '—'}</span>
                      </div>
                      <div className="summary-row total-row due-today-row">
                        <span className="row-label">
                          {'Due Today'}
                          {accessUntilFormatted && (
                            <span className="already-paid-tag">
                              {`Paid until ${accessUntilFormatted}`}
                            </span>
                          )}
                        </span>
                        <span className="row-value highlight-val zero-cost">
                          {`${currencySymbol}0.00`}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="summary-row total-row">
                      <span className="row-label">{'Total'}</span>
                      <span className="row-value highlight-val">
                        {previewData?.formatted_total || '—'}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Saved Card Snippet for Unexpired Reactivations */}
            {isPlanNotExpired && defaultCard && (
              <div className="reactivate-renewal-card-section">
                <span className="renewal-section-label">
                  {accessUntilFormatted
                    ? `Upcoming renewal on ${accessUntilFormatted} will be billed to:`
                    : 'Upcoming renewal will be billed to:'}
                </span>
                <div className="payment-card-row selected-card">
                  <div className="card-brand-wrap">
                    <CardBrandLogo brand={defaultCard.brand} />
                  </div>
                  <div className="card-info-text">
                    <span className="card-title-text">
                      {getCardBrandDisplayName(defaultCard.brand)}
                    </span>
                    <span className="card-mask">
                      {formatMaskedCardNumber(defaultCard.brand, defaultCard.last4)}
                    </span>
                  </div>
                  <span className="default-pill">{'Default'}</span>
                </div>
              </div>
            )}

            {/* Promo Code Input - Only needed for expired checkout/renewals */}
            {!isPlanNotExpired && (
              <div className="promo-section">
                {activePromo ? (
                  <div className="applied-promo-badge">
                    <span>
                      {`Promo applied: ${activePromo.code}`}
                      {backendPromoLabel}
                    </span>
                    <button
                      type="button"
                      onClick={handleRemovePromo}
                      aria-label="Remove promo code"
                    >
                      {'✕'}
                    </button>
                  </div>
                ) : (
                  <>
                    <label htmlFor="reactivate-promo-input">{'Add promo code'}</label>
                    <form
                      className="promo-input-row"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleApplyPromo();
                      }}
                    >
                      <div className="promo-input-field-wrap">
                        <input
                          id="reactivate-promo-input"
                          type="text"
                          placeholder="Code"
                          maxLength={30}
                          className={promoError ? 'has-error' : ''}
                          value={promoInput}
                          onChange={(e) => {
                            setPromoInput(e.target.value.toUpperCase());
                            if (promoError) setPromoError('');
                          }}
                          onBlur={(e) => setPromoInput(e.target.value.trim())}
                          autoComplete="off"
                        />
                        {promoInput.length > 0 && (
                          <button
                            type="button"
                            className="clear-promo-btn"
                            onClick={() => {
                              setPromoInput('');
                              if (promoError) setPromoError('');
                            }}
                            aria-label="Clear promo code"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <button type="submit" className="btn-add-promo" disabled={isValidatingPromo}>
                        {isValidatingPromo ? (
                          <Spin
                            indicator={
                              <LoadingOutlined style={{ fontSize: 13, color: '#060723' }} spin />
                            }
                          />
                        ) : (
                          'Add'
                        )}
                      </button>
                    </form>
                    {promoError && <p className="promo-error-text">{promoError}</p>}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Card 2: Billing Information - Only needed for expired renewals */}
          {!isPlanNotExpired && (
            <div className="reactivate-card billing-info-card">
              <div className="card-header-with-action">
                <h3>{'Billing Information'}</h3>
                <button
                  type="button"
                  className="icon-action-btn"
                  onClick={() => setIsBillingModalOpen(true)}
                  title="Edit billing info"
                >
                  <BillingInfoEditSVG />
                </button>
              </div>

              <div className="info-key-value-list">
                <div className="kv-item">
                  <span className="kv-key">{'Full Name'}</span>
                  <span className="kv-val">{fullNameDisplay}</span>
                </div>
                <div className="kv-item">
                  <span className="kv-key">{'Billing Email'}</span>
                  <span className="kv-val">{emailDisplay}</span>
                </div>
                <div className="kv-item">
                  <span className="kv-key">{'Address'}</span>
                  <span className="kv-val">{addressDisplay}</span>
                </div>
              </div>
            </div>
          )}

          {/* Card 3: Payment Method - Only needed for expired renewals */}
          {!isPlanNotExpired && (
            <div className="reactivate-card payment-method-card">
              <div className="card-header-with-action">
                <h3>{'Payment Method'}</h3>
                <button
                  type="button"
                  className="btn-add-mini"
                  onClick={() => setIsPaymentModalOpen(true)}
                >
                  <AddButtonSVG />
                  <span>{'Add'}</span>
                </button>
              </div>

              {defaultCard ? (
                <div className="payment-card-row selected-card">
                  <div className="card-brand-wrap">
                    <CardBrandLogo brand={defaultCard.brand} />
                  </div>
                  <div className="card-info-text">
                    <span className="card-title-text">
                      {getCardBrandDisplayName(defaultCard.brand)}
                    </span>
                    <span className="card-mask">
                      {formatMaskedCardNumber(defaultCard.brand, defaultCard.last4)}
                    </span>
                  </div>
                  <span className="default-pill">{'Default'}</span>
                </div>
              ) : (
                <p className="card-empty-description">
                  {'A payment method is required to reactivate Pro.'}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="reactivate-right-col">
          <div className="with-pro-card">
            <h3>{`With ${proPlan?.name ?? 'Pro'} you get`}</h3>

            <div className="features-list">
              {featuresList.map((feat) => (
                <div className="feature-item" key={feat}>
                  <CheckmarkSVG className="gold-check" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Security notice */}
          <div className="security-notice">
            <SecurePaymentLockSVG />
            <span>{'Your payment information is secure and encrypted.'}</span>
          </div>

          {/* Agreement checkbox */}
          <label className={`terms-agreement-checkbox ${agreedError ? 'has-error' : ''}`}>
            <input
              type="checkbox"
              checked={isAgreed}
              onChange={(e) => {
                setIsAgreed(e.target.checked);
                if (e.target.checked) setAgreedError(false);
              }}
            />
            <span>
              {'I agree to the '}
              <button
                type="button"
                className="legal-inline-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateLegal('terms-of-service');
                }}
              >
                {'Terms of Service'}
              </button>
              {' and '}
              <button
                type="button"
                className="legal-inline-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateLegal('privacy-policy');
                }}
              >
                {'Privacy Policy'}
              </button>
              {'.'}
            </span>
          </label>

          {/* CTA */}
          <button
            type="button"
            className="btn-reactivate-cta"
            onClick={handleReactivate}
            disabled={isSubmitting || isPreviewLoading || isPreviewError || !previewData}
          >
            {isSubmitting ? (
              <>
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 18, color: '#060723' }} spin />}
                />
                <span>{'Reactivating...'}</span>
              </>
            ) : isPreviewLoading ? (
              <span>{'Loading pricing...'}</span>
            ) : isPlanNotExpired ? (
              <span>{`Reactivate Pro — ${currencySymbol}0.00 Due Today`}</span>
            ) : (
              <span>{'Reactivate Pro'}</span>
            )}
          </button>

          <div className="need-help-link-wrap">
            <span>{'Need help? '}</span>
            <button type="button" className="help-link" onClick={handleGoToSupport}>
              {'Contact Support'}
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <BillingInfoModal
        isOpen={isBillingModalOpen}
        onClose={() => setIsBillingModalOpen(false)}
        onSuccess={() => {
          void refetchBillingProfile();
        }}
      />

      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSuccess={() => {
          void refetchPaymentMethods();
        }}
      />

      <PaymentConfirmedModal
        isOpen={isConfirmedModalOpen}
        onClose={() => setIsConfirmedModalOpen(false)}
        isTrialMode={false}
        amountFormatted={previewData?.formatted_total}
        dueTodayFormatted={
          isPlanNotExpired
            ? `${currencySymbol}0.00`
            : previewData?.formatted_due_today || previewData?.formatted_total
        }
        firstPaymentFormatted={previewData?.formatted_total}
        nextBillingDateFormatted={isPlanNotExpired ? accessUntilFormatted : undefined}
      />
    </div>
  );
};
